import React from 'react';
import { useLocale } from '../../hooks/useLocale';
import { useStoreActions, useStoreState } from '../../store';
import { SubtitleTextColors, SubtitleTextSizes, SubtitleTextWeights } from '../../typings';
import { Toggle } from '../Toggle';
import {
  BoldTextLabel,
  BoldTextRow,
  BoldTextStatus,
  MobileColorSwatch,
  MobileDivider,
  MobileOptionLabel,
  MobileOptionRow,
  MobileSectionTitle,
  RadioIndicator,
} from './styles';

const textSizeOptions = [
  SubtitleTextSizes.Smallest,
  SubtitleTextSizes.Small,
  SubtitleTextSizes.Normal,
  SubtitleTextSizes.Large,
  SubtitleTextSizes.Largest,
];

const textColorOptions = [
  SubtitleTextColors.White,
  SubtitleTextColors.Gray,
  SubtitleTextColors.Black,
  SubtitleTextColors.Yellow,
  SubtitleTextColors.Orange,
  SubtitleTextColors.Red,
  SubtitleTextColors.Green,
  SubtitleTextColors.Cyan,
  SubtitleTextColors.Blue,
];

export const MobileOptionsScreen = () => {
  const locale = useLocale();
  const actions = useStoreActions((actions) => actions);
  const subtitleTextWeight = useStoreState((state) => state.subtitles.subtitleTextWeight);
  const subtitleTextSize = useStoreState((state) => state.subtitles.subtitleTextSize);
  const subtitleTextColor = useStoreState((state) => state.subtitles.subtitleTextColor);

  function handleWeightChange() {
    const { Bold, Regular } = SubtitleTextWeights;
    const weight = subtitleTextWeight === Bold ? Regular : Bold;
    actions.subtitles.setSubtitleTextWeight(weight);
    actions.settings.setSubtitleTextWeight(weight);
  }

  function handleSizeClick(size: SubtitleTextSizes) {
    actions.subtitles.setSubtitleTextSize(size);
    actions.settings.setSubtitleTextSize(size);
  }

  function handleColorClick(colorOption: SubtitleTextColors) {
    actions.subtitles.setSubtitleTextColor(colorOption);
    actions.settings.setSubtitleTextColor(colorOption);
  }

  return (
    <>
      <BoldTextRow>
        <BoldTextLabel>{locale.translations.SubtitleOptions.BoldText}</BoldTextLabel>
        <BoldTextStatus>
          {subtitleTextWeight === SubtitleTextWeights.Bold
            ? locale.translations.General.On
            : locale.translations.General.Off}
        </BoldTextStatus>
        <Toggle
          active={subtitleTextWeight === SubtitleTextWeights.Bold}
          onClick={handleWeightChange}
          data-testid="videoPlayer_subtitleTextBoldCheckbox"
        />
      </BoldTextRow>

      <MobileDivider />

      <MobileSectionTitle>{locale.translations.SubtitleOptions.FontSize}</MobileSectionTitle>
      {textSizeOptions.map((textSizeOption) => {
        const isActive = subtitleTextSize === textSizeOption;
        return (
          <MobileOptionRow key={textSizeOption} onClick={() => handleSizeClick(textSizeOption)}>
            <MobileOptionLabel active={isActive}>
              {locale.translations.SubtitleOptions.TextSizeOption(textSizeOption)}
            </MobileOptionLabel>
            <RadioIndicator active={isActive} />
          </MobileOptionRow>
        );
      })}

      <MobileDivider />

      <MobileSectionTitle>{locale.translations.SubtitleOptions.FontColor}</MobileSectionTitle>
      {textColorOptions.map((colorOption) => {
        const isActive = colorOption === subtitleTextColor;
        return (
          <MobileOptionRow
            key={colorOption}
            data-testid={`videoPlayer_subtitleColor_${colorOption}`}
            onClick={() => handleColorClick(colorOption)}
          >
            <MobileColorSwatch swatchColor={colorOption} />
            <MobileOptionLabel active={isActive}>
              {locale.translations.SubtitleOptions.TextColorOption(colorOption)}
            </MobileOptionLabel>
            <RadioIndicator active={isActive} />
          </MobileOptionRow>
        );
      })}
    </>
  );
};
